import { useEffect, useMemo } from "react";
import { AxiosResponse } from "axios";

import { useRequest } from "../../../hooks/useRequest";
import routes from "../../../routes";
import { ParsedProspect, parseProspectList } from "../parser";
import { getPrestoneReportUrl } from "../prospect-details/model";
import { deriveMultiSelectData } from "../prospect-list/filterOptions";
import {
  buildProspectTableModel,
  prestoneReportField,
} from "../prospect-list/model";
import { createProspectColumns } from "../prospect-list/utils";

function useProspectList() {
  const { loading, fetch, response, error } = useRequest();

  useEffect(() => {
    fetch("GET", routes.api.prospects.base({}));
  }, [fetch]);

  const getProspectList = (prospects: AxiosResponse | undefined): unknown => {
    return prospects?.data ?? [];
  };
  const { rows, fieldDefinitions, columns, parsingError } = useMemo(() => {
    const rawProspectList = getProspectList(response);
    let nextParsingError: Error | null = null;
    let prospectList: ParsedProspect[] = [];

    if (response?.data && !error) {
      try {
        prospectList = parseProspectList(rawProspectList);
      } catch (parseError) {
        nextParsingError =
          parseError instanceof Error
            ? parseError
            : new Error("Invalid prospect payload");
      }
    }

    const { rows: nextRows, fieldDefinitions } =
      buildProspectTableModel(prospectList);

    const multiSelectData = deriveMultiSelectData(prospectList);
    const prestoneIds = new Map(
      prospectList.map(({ submissionId, prestoneId }) => [
        submissionId,
        prestoneId,
      ]),
    );

    return {
      rows: nextRows.map((row) => {
        const prestoneId = prestoneIds.get(row.id);

        return {
          ...row,
          [prestoneReportField]: prestoneId
            ? getPrestoneReportUrl(prestoneId)
            : null,
        };
      }),
      fieldDefinitions,
      columns: createProspectColumns(fieldDefinitions, multiSelectData),
      parsingError: nextParsingError,
    };
  }, [response, error]);

  return {
    loading,
    hasResolved: Boolean(response || error),
    rows,
    fieldDefinitions,
    columns,
    error: parsingError ?? error,
  };
}

export default useProspectList;
